import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ClipboardCheck, ChevronRight, Heart, MessageCircle, Handshake, Users, Shield, AlertOctagon, HeartCrack, Baby, Sparkles } from 'lucide-react';

const QUESTIONS = [
  {
    id: 1,
    question: 'Hur skulle du beskriva kommunikationen i dina nära relationer?',
    options: [
      { text: 'Vi pratar öppet och lyssnar på varandra', score: { communication: 0 } },
      { text: 'Ibland missförstår vi varandra', score: { communication: 1 } },
      { text: 'Vi undviker ofta svåra samtal', score: { communication: 2 } },
      { text: 'Samtalen leder ofta till bråk', score: { communication: 2, conflict: 1 } },
    ]
  },
  {
    id: 2,
    question: 'Hur hanterar du konflikter i relationer?',
    options: [
      { text: 'Vi löser dem konstruktivt tillsammans', score: { conflict: 0 } },
      { text: 'Jag undviker konflikter så mycket jag kan', score: { conflict: 2 } },
      { text: 'Konflikter eskalerar ofta', score: { conflict: 2, toxic: 1 } },
      { text: 'Jag ger ofta efter för att undvika bråk', score: { conflict: 1, boundaries: 1 } },
    ]
  },
  {
    id: 3,
    question: 'Hur lätt är det för dig att sätta gränser?',
    options: [
      { text: 'Jag är bra på att säga nej när jag behöver', score: { boundaries: 0 } },
      { text: 'Ibland säger jag ja fast jag inte vill', score: { boundaries: 1 } },
      { text: 'Jag har svårt att säga nej och känner skuld', score: { boundaries: 2 } },
      { text: 'Mina gränser respekteras sällan', score: { boundaries: 2, toxic: 1 } },
    ]
  },
  {
    id: 4,
    question: 'Hur mår du i din nuvarande parrelation (eller senaste)?',
    options: [
      { text: 'Jag känner mig trygg och älskad', score: { couple: 0 } },
      { text: 'Det finns utrymme för förbättring', score: { couple: 1 } },
      { text: 'Vi har tappat gnistan och närheten', score: { couple: 2 } },
      { text: 'Relationen känns osund eller tärande', score: { toxic: 2 } },
      { text: 'Jag är singel / går igenom separation', score: { separation: 2 } },
    ]
  },
  {
    id: 5,
    question: 'Hur är din relation till din ursprungsfamilj?',
    options: [
      { text: 'Nära och stöttande', score: { family: 0 } },
      { text: 'Okej men med vissa spänningar', score: { family: 1 } },
      { text: 'Komplicerad med olösta konflikter', score: { family: 2 } },
      { text: 'Jag har behövt distansera mig', score: { family: 2, boundaries: 1 } },
    ]
  },
  {
    id: 6,
    question: 'Hur upplever du dina vänskaper?',
    options: [
      { text: 'Jag har nära vänner som stöttar mig', score: { friendship: 0 } },
      { text: 'Jag önskar att jag hade fler nära vänner', score: { friendship: 2 } },
      { text: 'Jag har svårt att underhålla vänskaper', score: { friendship: 2 } },
      { text: 'Jag känner mig ofta ensam', score: { friendship: 2 } },
    ]
  },
  {
    id: 7,
    question: 'Har du upplevt eller upplever du tecken på en destruktiv relation?',
    options: [
      { text: 'Nej, inte vad jag vet', score: { toxic: 0 } },
      { text: 'Jag är osäker, vill lära mig mer', score: { toxic: 1 } },
      { text: 'Ja, i en tidigare relation', score: { toxic: 2 } },
      { text: 'Ja, i en pågående relation', score: { toxic: 3 } },
    ]
  },
  {
    id: 8,
    question: 'Hur känner du inför förändringar eller avslut i relationer?',
    options: [
      { text: 'Jag hanterar dem relativt bra', score: { separation: 0 } },
      { text: 'De är svåra men jag tar mig igenom', score: { separation: 1 } },
      { text: 'Jag går igenom en separation just nu', score: { separation: 3 } },
      { text: 'Jag har svårt att släppa taget', score: { separation: 2 } },
    ]
  },
];

const RESULTS_MAP = {
  communication: {
    title: 'Kommunikation',
    icon: MessageCircle,
    path: '/communication',
    color: 'bg-blue-100 text-blue-600',
    description: 'Lär dig uttrycka dig tydligt och lyssna aktivt'
  },
  conflict: {
    title: 'Konflikthantering',
    icon: Handshake,
    path: '/conflict',
    color: 'bg-amber-100 text-amber-600',
    description: 'Hantera bråk konstruktivt och reparera efteråt'
  },
  boundaries: {
    title: 'Gränser',
    icon: Shield,
    path: '/boundaries',
    color: 'bg-purple-100 text-purple-600',
    description: 'Lär dig sätta hälsosamma gränser'
  },
  couple: {
    title: 'Parövningar',
    icon: Heart,
    path: '/couple-exercises',
    color: 'bg-pink-100 text-pink-600',
    description: 'Stärk er relation med guidade övningar'
  },
  family: {
    title: 'Familj',
    icon: Baby,
    path: '/family',
    color: 'bg-rose-100 text-rose-600',
    description: 'Utforska familjedynamik och läka sår'
  },
  friendship: {
    title: 'Vänskap',
    icon: Users,
    path: '/friendship',
    color: 'bg-teal-100 text-teal-600',
    description: 'Hitta och vårda meningsfulla vänskaper'
  },
  toxic: {
    title: 'Toxiska relationer',
    icon: AlertOctagon,
    path: '/toxic-relationships',
    color: 'bg-red-100 text-red-600',
    description: 'Identifiera destruktiva mönster och skydda dig'
  },
  separation: {
    title: 'Separation',
    icon: HeartCrack,
    path: '/separation',
    color: 'bg-slate-100 text-slate-600',
    description: 'Hantera avslut hälsosamt och bygg upp dig'
  },
};

export default function RelationshipCheckPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState({
    communication: 0,
    conflict: 0,
    boundaries: 0,
    couple: 0,
    family: 0,
    friendship: 0,
    toxic: 0,
    separation: 0,
  });
  const [showResults, setShowResults] = useState(false);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="relationship-check-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Relationshälso-check</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till testet.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  const handleAnswer = (option) => {
    // Update answers
    const newAnswers = { ...answers, [currentQuestion]: option };
    setAnswers(newAnswers);

    // Update scores
    const newScores = { ...scores };
    Object.entries(option.score).forEach(([key, value]) => {
      newScores[key] = (newScores[key] || 0) + value;
    });
    setScores(newScores);

    // Move to next question or show results
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const getTopResults = () => {
    // Sort by score and get top 3 recommendations
    const sorted = Object.entries(scores)
      .filter(([_, score]) => score > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    
    return sorted.map(([key]) => RESULTS_MAP[key]);
  };

  const resetTest = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setScores({
      communication: 0,
      conflict: 0,
      boundaries: 0,
      couple: 0,
      family: 0,
      friendship: 0,
      toxic: 0,
      separation: 0,
    });
    setShowResults(false);
  };

  // Results view
  if (showResults) {
    const topResults = getTopResults();
    const hasUrgentResult = scores.toxic >= 3;

    return (
      <div className="min-h-screen bg-background" data-testid="relationship-check-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Ditt resultat</h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="text-center space-y-3">
            <Sparkles className="w-12 h-12 mx-auto text-primary" />
            <h2 className="font-heading text-2xl font-semibold">Din relationshälso-check</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Baserat på dina svar rekommenderar vi att du utforskar dessa områden:
            </p>
          </div>

          {/* Urgent warning if toxic score is high */}
          {hasUrgentResult && (
            <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
              <p className="text-red-800 text-sm font-semibold mb-2">🆘 Viktigt</p>
              <p className="text-red-700 text-sm">
                Dina svar tyder på att du kan befinna dig i en osund relation. Vi rekommenderar starkt att du läser vår sektion om toxiska relationer och överväger att söka professionell hjälp.
              </p>
              <ul className="text-red-700 text-sm mt-2 space-y-1">
                <li>• <strong>Brottsofferjouren:</strong> 116 006 (alla)</li>
                <li>• <strong>Kvinnofridslinjen:</strong> 020-50 50 50</li>
                <li>• <strong>Mansjouren:</strong> 08-30 30 20</li>
                <li>• <strong>Polisen:</strong> 112 (akut) / 114 14</li>
              </ul>
            </div>
          )}

          {/* Top recommendations */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Rekommenderade områden</h3>
            {topResults.length > 0 ? (
              topResults.map((result, index) => (
                <button
                  key={result.path}
                  onClick={() => navigate(result.path)}
                  className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                        {index + 1}
                      </span>
                      <div className={`w-12 h-12 rounded-xl ${result.color} flex items-center justify-center`}>
                        <result.icon className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{result.title}</h4>
                      <p className="text-sm text-muted-foreground">{result.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                </button>
              ))
            ) : (
              <div className="bg-green-50 rounded-2xl p-6 text-center">
                <Heart className="w-10 h-10 mx-auto text-green-500 mb-3" />
                <h4 className="font-semibold text-green-800">Bra jobbat!</h4>
                <p className="text-green-700 text-sm mt-1">
                  Dina svar tyder på att du har en god relationshälsa. Fortsätt utforska våra resurser för att fördjupa dina relationer ytterligare.
                </p>
              </div>
            )}
          </div>

          {/* All areas */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Utforska alla områden</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.values(RESULTS_MAP).map((result) => (
                <button
                  key={result.path}
                  onClick={() => navigate(result.path)}
                  className="bg-white p-4 rounded-xl border border-border/30 text-center hover:shadow-md transition-all group"
                >
                  <div className={`w-10 h-10 mx-auto rounded-lg ${result.color} flex items-center justify-center mb-2`}>
                    <result.icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-medium">{result.title}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button onClick={resetTest} variant="outline" className="flex-1 rounded-full">
              Gör testet igen
            </Button>
            <Button onClick={() => navigate('/category/relations')} className="flex-1 rounded-full">
              Tillbaka till startsidan
            </Button>
          </div>

          
        </main>
      </div>
    );
  }

  // Question view
  const question = QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  return (
    <div className="min-h-screen bg-background" data-testid="relationship-check-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Relationshälso-check</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Fråga {currentQuestion + 1} av {QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="bg-white rounded-2xl p-8 border border-border/30">
          <h2 className="font-heading text-xl font-semibold mb-6 text-center">
            {question.question}
          </h2>

          <div className="space-y-3">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className="w-full p-4 rounded-xl border border-border/50 text-left hover:border-primary hover:bg-primary/5 transition-all"
              >
                <span className="text-foreground">{option.text}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <p className="text-xs text-muted-foreground text-center">
          Dina svar sparas inte och är helt anonyma. Testet är endast vägledande.
        </p>
      </main>
    </div>
  );
}
